// core
import React, { useState } from "react";
import * as Yup from "yup";

// components
import LModal from "../../../../common/components/Modals/LModal/LModal";
import ModalHeader from "../../../../common/components/Modals/ModalHeader/ModalHeader";
import ContactUsModal from "../../Modals/ContactUsModal/ContactUsModal";

// providers
import { Form, Formik } from "formik";
import Input from "../../../../common/components/Forms/Input/Input";
import Button from "../../../../common/components/Forms/Button/Button";
import Alert from "../../../../common/components/Alert/Alert";

//enums
import { Severity } from "../../../../common/interfaces/enums/Severity";

type LifeCoverCalculatorForm = {
    debt: number | null;
    income: number | null;
    years: number | null;
    costs: number | null;
    assets: number | null;
};

const initialValues: LifeCoverCalculatorForm = {
    debt: null,
    income: null,
    years: null,
    costs: null,
    assets: null
};

/*
 * See https://www.ird.govt.nz/income-tax/income-tax-for-individuals/tax-codes-and-tax-rates-for-individuals/tax-rates-for-individuals
 *  for latest tax band information. NOTE: A simplified update will come into effect on 1 April 2025.
 */
const GetAfterTaxIncome = (grossIncome: number): number => {
    const taxBrackets = [
        { top: 14000, rate: 1.0 - 0.105 },
        { top: 15600, rate: 1.0 - 0.1282 },
        { top: 48000, rate: 1.0 - 0.175 },
        { top: 53500, rate: 1.0 - 0.2164 },
        { top: 70000, rate: 1.0 - 0.30 },
        { top: 78100, rate: 1.0 - 0.3099 },
        { top: 180000, rate: 1.0 - 0.33 }
    ];
    const maxTaxRate = 1.0 - 0.39;

    var cumulativeIncome = 0.0;
    var previousBracket = 0;

    for (var i = 0; i < taxBrackets.length; i++) {
        var currentBracket = taxBrackets[i];
        if (grossIncome <= currentBracket.top) {
            return cumulativeIncome + (grossIncome - previousBracket) * currentBracket.rate;
        }

        cumulativeIncome += (currentBracket.top - previousBracket) * currentBracket.rate;
        previousBracket = currentBracket.top;
    }

    return cumulativeIncome + (grossIncome - previousBracket) * maxTaxRate;
};


const CalculateTotalReplacementIncome = (monthlyIncome: number, payoutMonths: number, monthlyInflationRate: number): number => {
    if (monthlyInflationRate == 0) return monthlyIncome * payoutMonths;
    return monthlyIncome * payoutMonths * Math.pow((monthlyInflationRate + 1) / 2, payoutMonths - 1);   // Approximates sum of monthly income over payout months
};

const titles: {
    [key in keyof LifeCoverCalculatorForm]: {
        title: string;
        placeholder?: string;
        isCurrency?: boolean;
        tooltip?: string;
        errorMessage: string;
    };
} = {
    debt: {
        title: "How much debt do you have?",
        isCurrency: true,
        tooltip:
            "If your debts (for example a mortgage) would be a burden on others if you passed away, consider covering them with Life Cover.",
        errorMessage: "Please specify your debt."
    },
    income: {
        title: "How much do you earn per year?",
        placeholder: "Gross income",
        isCurrency: true,
        tooltip:
            "Are your family financially dependent on your income? Use your Life Cover to provide a replacement income - for as few or as many years as your family would need.",
        errorMessage: "Please specify your gross annual income."
    },
    years: {
        title:
            "How many years would you want your income to continue if you passed away?",
        placeholder: "Years",
        errorMessage: "Please specify how many years of income you want to be covered."
    },
    costs: {
        title: "How much would you need to cover other costs?",
        isCurrency: true,
        tooltip:
            "You can use life insurance to cover things like children's education costs, funeral costs - or anything else you'd like.",
        errorMessage: "Please specify an amount for other costs."
    },
    assets: {
        title:
            "Total value of assets or spare money you have (this could reduce the amount of cover you require)",
        isCurrency: true,
        tooltip:
            "Do you have large savings, assets to sell, or existing insurance? They could be used to reduce the amount of Life Cover you need.",
        errorMessage: "Please specify the value of your assets."
    }
};

const calculateCoverAmount = (values: LifeCoverCalculatorForm): number => {
    const { debt, income, costs, assets, years } = values;

    const monthlyInflationRate = Math.exp(Math.log(1.02) / 12);

    const netIncome = GetAfterTaxIncome(income);
    const replacementIncome = CalculateTotalReplacementIncome(netIncome / 12, years * 12, monthlyInflationRate);

    return Math.max(Math.floor(debt + costs + replacementIncome - assets), 0);
};

interface ILifeCalculatorModal {
    isOpen: boolean;
    onClose: (coverAmount?: number) => void;
}

const validationSchema = (): Yup.ObjectSchema => {
    const validationObject = {};

    Object.keys(initialValues).forEach((x) => {
        const errorMessage = !titles[x]
            ? "Please enter a valid number."
            : titles[x].errorMessage;

        validationObject[x] = Yup.number().nullable().required(errorMessage);
    });

    return Yup.object(validationObject);
};

const LifeCalculatorModal: React.FC<ILifeCalculatorModal> = (props) => {
    const { isOpen, onClose } = props;

    const [isOpenContactUsModal, setIsOpenContactUsModal] = useState(false);

    return (
        <React.Fragment>
            <LModal
                Header={() => (
                    <ModalHeader
                        onClose={() => onClose()}
                        title="Life cover calculator"
                    />
                )}
                calcHeightMode="auto"
                width="narrow"
                isOpen={isOpen}
                onClose={() => onClose()}>
                {() => (
                    <React.Fragment>
                        <Formik
                            initialValues={initialValues}
                            onSubmit={(values) => {
                                onClose(calculateCoverAmount(values));
                            }}
                            validationSchema={validationSchema()}>
                            {({
                                values,
                                setValues,
                                handleBlur,
                                touched,
                                errors,
                                dirty
                            }) => {
                                const coverAmount =
                                    !dirty || Object.values(errors).length
                                        ? -1
                                        : calculateCoverAmount(values);

                                const renderCoverAmount = (): React.ReactNode => {
                                    return (
                                        <React.Fragment>
                                            {"Based on your info, you would need "}
                                            <strong className="text-pink">
                                                {"$" + coverAmount.toLocaleString()}
                                            </strong>{" "}
                                            {" of life cover."}
                                        </React.Fragment>
                                    );
                                };

                                return (
                                    <React.Fragment>
                                        <Form>
                                            {Object.keys(initialValues).map((key) => {
                                                const metaData = titles[key];

                                                return (
                                                    <div
                                                        key={key}
                                                        className="layout-flex-row">
                                                        <Input
                                                            name={key}
                                                            type="number"
                                                            hasCurrency={
                                                                metaData.isCurrency
                                                            }
                                                            value={values[key]}
                                                            label={metaData.title}
                                                            placeholder={
                                                                metaData.placeholder
                                                            }
                                                            onChange={(e) =>
                                                                setValues({
                                                                    ...values,
                                                                    [key]: e
                                                                })
                                                            }
                                                            onBlur={handleBlur}
                                                            hasError={
                                                                touched[key] &&
                                                                errors[key]
                                                            }
                                                            tooltipContent={
                                                                metaData.tooltip
                                                            }></Input>
                                                    </div>
                                                );
                                            })}

                                            <p className="gap-ml text-large-message">
                                                {" "}
                                                {!isNaN(coverAmount) && coverAmount >= 0
                                                    ? renderCoverAmount()
                                                    : "Please enter all fields in the calculator."}
                                            </p>

                                            <Button
                                                component="button"
                                                type="submit"
                                                variant="calltoaction"
                                                size="m"
                                                className="gap-pre-l gap-xl"
                                                isDisabled={!coverAmount}>
                                                Use this amount
                                            </Button>
                                        </Form>

                                        {coverAmount >= 0 && coverAmount < 100000 && (
                                            <React.Fragment>
                                                <ContactUsModal
                                                    isOpen={isOpenContactUsModal}
                                                    onClose={() =>
                                                        setIsOpenContactUsModal(false)
                                                    }
                                                />

                                                <Alert
                                                    severity={Severity.Info}
                                                    className="gap-ml">
                                                    <strong>
                                                        This calculator is designed for a
                                                        minimum cover amount of $100,000.
                                                    </strong>{" "}
                                                    Please{" "}
                                                    <a
                                                        onClick={() => {
                                                            setIsOpenContactUsModal(true);
                                                        }}>
                                                        contact us
                                                    </a>{" "}
                                                    if you need some assistance or advice.
                                                </Alert>
                                            </React.Fragment>
                                        )}
                                    </React.Fragment>
                                );
                            }}
                        </Formik>

                        <p className="text-small text-light">
                            <strong className="gap-m"> Disclaimer: </strong>
                            This calculator is designed as a quick guide to help you work out what cover level might be right for you.
                            It assumes an annual inflation rate of 2%.
                            This is not financial advice or a personal recommendation.
                            It does not take into consideration your financial situation nor the details relevant to insurance.
                            This is based on limited details and is not a quote of an offer of insurance.
                        </p>
                    </React.Fragment>
                )}
            </LModal>
        </React.Fragment>
    );
};

export default LifeCalculatorModal;
